import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Chip,
  Divider,
  Fab,
  Tooltip,
} from "@mui/material";
import {
  Save,
  ArrowBack,
  Person,
  Bed,
  LocalHospital,
  Description,
  NavigateNext,
  NavigateBefore,
  CheckCircle,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createIPDAdmission,
  getAvailableBeds,
  listIPDAdmissions,
} from "../../../../api/ipd";
import { getEmergencyVisit } from "../../../../api/emergency";
import useFetchObjects from "../../../../api/useFetchObjects";
import TicketSearch from "../../../../Components/TicketSearch";
import TicketSelectionView from "../../../../Components/TicketSelectionView";
import axios from "axios";

const NewAdmission = () => {
  const { t } = useTranslation();
  
  const steps = [
    t("ipd.admissions.steps.patient_selection", "Patient Selection"),
    t("ipd.admissions.steps.bed_assignment", "Bed Assignment"),
    t("ipd.admissions.steps.admission_details", "Admission Details"),
    t("ipd.admissions.steps.review", "Review"),
  ];
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const emergencyVisitId = searchParams.get("emergency");

  // State
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    patient: null,
    bed: null,
    admission_type: emergencyVisitId ? 1 : 2, // Emergency if from emergency, otherwise Elective
    admission_diagnosis: "",
    admitting_doctor: auth?.user?.user?.id || "",
    department: "",
    expected_discharge_date: "",
    admission_notes: "",
    emergency_visit: null,
    ticket: null,
  });
  const [selectedTicket, setSelectedTicket] = useState(null);

  const [errors, setErrors] = useState({});
  const [emergencyVisit, setEmergencyVisit] = useState(null);
  const [loadingEmergency, setLoadingEmergency] = useState(false);

  // Fetch data with better error handling
  const {
    data: patients,
    isLoading: patientsLoading,
    error: patientsError,
  } = useFetchObjects(["patients"], "patients/?page_size=1000", token);

  const {
    data: doctors,
    isLoading: doctorsLoading,
    error: doctorsError,
  } = useFetchObjects(
    ["users"],
    "users/?page_size=1000", // Get all users, we'll filter doctors on frontend
    token
  );

  const {
    data: departments,
    isLoading: departmentsLoading,
    error: departmentsError,
  } = useFetchObjects(["departments"], "departments/?page_size=1000", token);

  const {
    data: availableBeds,
    isLoading: bedsLoading,
    error: bedsError,
  } = useQuery({
    queryKey: ["available-beds"],
    queryFn: () => getAvailableBeds({}, token),
  });

  // Filter doctors - since we don't have position/is_doctor fields, show all users for now
  // TODO: Add proper doctor identification field in backend
  const filteredDoctors = Array.isArray(doctors)
    ? doctors
    : doctors?.results || [];

  // Fetch emergency visit data if coming from Emergency
  useEffect(() => {
    const fetchEmergencyVisit = async () => {
      if (!emergencyVisitId || !token) return;

      try {
        setLoadingEmergency(true);
        const emergencyData = await getEmergencyVisit(emergencyVisitId, token);
        setEmergencyVisit(emergencyData);

        // Pre-fill form data from emergency visit
        if (emergencyData) {
          // Find patient in patients list
          const patientList = Array.isArray(patients)
            ? patients
            : patients?.results || [];
          const matchedPatient = emergencyData.patient
            ? patientList.find((p) => p.id === emergencyData.patient)
            : null;

          // Find attending doctor in doctors list
          const doctorsList = Array.isArray(doctors)
            ? doctors
            : doctors?.results || [];
          const matchedDoctor = emergencyData.attending_doctor
            ? doctorsList.find((d) => d.id === emergencyData.attending_doctor)
            : null;

          // Pre-fill form data
          setFormData((prev) => ({
            ...prev,
            patient: matchedPatient || null,
            admission_diagnosis: emergencyData.diagnosis || "",
            admitting_doctor: emergencyData.attending_doctor || prev.admitting_doctor,
            emergency_visit: emergencyData,
            ticket: emergencyData.ticket || null, // Pass ticket from emergency visit
            admission_type: 1, // Emergency admission type
            admission_notes: emergencyData.chief_complaint
              ? `Chief Complaint: ${emergencyData.chief_complaint}`
              : prev.admission_notes,
          }));
          
          // If emergency visit has a ticket, set it in selectedTicket for display
          if (emergencyData.ticket) {
            // Fetch ticket details to show in TicketSelectionView
            try {
              const ticketResponse = await axios.get(
                `${process.env.REACT_APP_API_URL || ""}/tickets/${emergencyData.ticket}/`,
                {
                  headers: { Authorization: `Token ${token}` },
                }
              );
              setSelectedTicket(ticketResponse.data);
            } catch (err) {
              console.error("Error fetching ticket:", err);
            }
          }

          // Skip to Step 1 (Bed Selection) if patient is found
          if (matchedPatient) {
            setActiveStep(1);
          }
        }
      } catch (error) {
        console.error("Error fetching emergency visit:", error);
      } finally {
        setLoadingEmergency(false);
      }
    };

    // Wait for patients and doctors to load before fetching emergency
    if (
      emergencyVisitId &&
      token &&
      !patientsLoading &&
      !doctorsLoading &&
      patients &&
      doctors
    ) {
      fetchEmergencyVisit();
    }
  }, [
    emergencyVisitId,
    token,
    patients,
    doctors,
    patientsLoading,
    doctorsLoading,
  ]);

  // Create admission mutation
  const createMutation = useMutation({
    mutationFn: (data) => createIPDAdmission(data, token),
    onSuccess: () => {
      // Invalidate and refetch admissions list
      queryClient.invalidateQueries({ queryKey: ["ipd-admissions"] });
      queryClient.invalidateQueries({ queryKey: ["ipd-dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["available-beds"] });
      navigate("/ipd/admissions");
    },
  });

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Patient Selection
        if (!formData.patient) {
          newErrors.patient = t("ipd.admissions.errors.patient_required");
        }
        break;
      case 1: // Bed Assignment
        if (!formData.bed) {
          newErrors.bed = t("ipd.admissions.errors.bed_required");
        }
        break;
      case 2: // Admission Details
        if (!formData.admission_diagnosis) {
          newErrors.admission_diagnosis = t(
            "ipd.admissions.errors.diagnosis_required"
          );
        }
        if (!formData.admitting_doctor) {
          newErrors.admitting_doctor = t(
            "ipd.admissions.errors.doctor_required"
          );
        }
        if (!formData.department) {
          newErrors.department = t("ipd.admissions.errors.department_required");
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = () => {
    if (validateStep(activeStep)) {
      // Transform the data to match backend expectations
      const submitData = {
        ...formData,
        patient: formData.patient?.id, // Send only the patient ID
        bed: formData.bed?.id, // Send only the bed ID
        admitting_doctor: formData.admitting_doctor, // This is already an ID
        department: formData.department, // This is already an ID
        emergency_visit: formData.emergency_visit?.id || null, // Send only ID if exists
        ticket: formData.ticket || null, // Send ticket ID if exists
      };

      createMutation.mutate(submitData);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("ipd.admissions.select_patient")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("ipd.admissions.ticket_search_hint", "Search by ticket number (optional) or select patient directly")}
              </Typography>
              
              {/* Ticket Search */}
              <Box sx={{ mb: 3 }}>
                <TicketSearch
                  value={selectedTicket}
                  onChange={(ticket) => {
                    setSelectedTicket(ticket);
                    if (ticket && ticket.patient) {
                      // Find patient from ticket
                      const patient = patients?.results?.find(p => p.id === ticket.patient);
                      if (patient) {
                        setFormData({ 
                          ...formData, 
                          patient: patient,
                          ticket: ticket.id,
                          department: ticket.department || formData.department
                        });
                        setErrors({ ...errors, patient: "" });
                      }
                    } else {
                      setFormData({ ...formData, ticket: null });
                    }
                  }}
                  label={t("ipd.admissions.ticket_number", "Ticket Number (Optional)")}
                  placeholder={t("ipd.admissions.search_ticket", "Search by ticket number...")}
                />
              </Box>

              {selectedTicket && (
                <Box sx={{ mb: 3 }}>
                  <TicketSelectionView ticket={selectedTicket} />
                </Box>
              )}

              {/* Patient Search (fallback if no ticket) */}
              {patientsLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : patientsError ? (
                <Alert severity="error">
                  {t("ipd.admissions.error_loading")} - {patientsError.message}
                </Alert>
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                    {t("ipd.admissions.or_select_patient", "Or select patient directly:")}
                  </Typography>
                  <Autocomplete
                    options={patients?.results || []}
                    getOptionLabel={(option) =>
                      `${option.firstname} ${option.lastname || ""}`
                    }
                    value={formData.patient}
                    onChange={(event, newValue) => {
                      setFormData({ ...formData, patient: newValue, ticket: null });
                      setSelectedTicket(null);
                      setErrors({ ...errors, patient: "" });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("ipd.admissions.patient")}
                        error={!!errors.patient}
                        helperText={errors.patient}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <Person />,
                        }}
                      />
                    )}
                  />
                </>
              )}
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("ipd.admissions.select_bed")}
              </Typography>
              {bedsLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : bedsError ? (
                <Alert severity="error">
                  {t("ipd.admissions.error_loading")} - {bedsError.message}
                </Alert>
              ) : !availableBeds || availableBeds.length === 0 ? (
                <Alert severity="warning">
                  No available beds found. Please contact the administrator.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {availableBeds?.map((bed) => (
                    <Grid item xs={12} sm={6} md={4} key={bed.id}>
                      <Card
                        sx={{
                          cursor: "pointer",
                          border: formData.bed?.id === bed.id ? 2 : 1,
                          borderColor:
                            formData.bed?.id === bed.id
                              ? "primary.main"
                              : "divider",
                          "&:hover": {
                            borderColor: "primary.main",
                            transform: "translateY(-2px)",
                            boxShadow: 3,
                          },
                          transition: "all 0.2s ease-in-out",
                        }}
                        onClick={() => {
                          setFormData({ ...formData, bed: bed });
                          setErrors({ ...errors, bed: "" });
                        }}
                      >
                        <CardContent>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={1}
                          >
                            <Bed color="primary" />
                            <Typography variant="h6">
                              {bed.bed_number}
                            </Typography>
                            {formData.bed?.id === bed.id && (
                              <CheckCircle color="primary" fontSize="small" />
                            )}
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            Room: {bed.room_number} | Ward: {bed.ward_name}
                          </Typography>
                          <Box display="flex" gap={1} flexWrap="wrap">
                            <Chip
                              label={bed.room_type}
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                            <Chip
                              label={`${bed.price || "N/A"} AFN`}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
              {errors.bed && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {errors.bed}
                </Alert>
              )}
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.admission_type}>
                <InputLabel>{t("ipd.admissions.type_label")}</InputLabel>
                <Select
                  value={formData.admission_type}
                  onChange={(e) =>
                    setFormData({ ...formData, admission_type: e.target.value })
                  }
                >
                  <MenuItem value={1}>
                    {t("ipd.admissions.type.emergency")}
                  </MenuItem>
                  <MenuItem value={2}>
                    {t("ipd.admissions.type.elective")}
                  </MenuItem>
                  <MenuItem value={3}>
                    {t("ipd.admissions.type.transfer")}
                  </MenuItem>
                  <MenuItem value={4}>
                    {t("ipd.admissions.type.observation")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.admitting_doctor}>
                <InputLabel>{t("ipd.admissions.admitting_doctor")}</InputLabel>
                <Select
                  value={formData.admitting_doctor}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      admitting_doctor: e.target.value,
                    })
                  }
                  disabled={doctorsLoading}
                >
                  {doctorsLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading doctors...
                    </MenuItem>
                  ) : filteredDoctors.length === 0 ? (
                    <MenuItem disabled>No users found</MenuItem>
                  ) : (
                    filteredDoctors.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                        {user.username && ` (${user.username})`}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {doctorsError && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    Error loading doctors: {doctorsError.message}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.department}>
                <InputLabel>{t("ipd.admissions.department")}</InputLabel>
                <Select
                  value={formData.department}
                  onChange={(e) =>
                    setFormData({ ...formData, department: e.target.value })
                  }
                  disabled={departmentsLoading}
                >
                  {departmentsLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading departments...
                    </MenuItem>
                  ) : !departments ||
                    (Array.isArray(departments)
                      ? departments.length === 0
                      : departments.results?.length === 0) ? (
                    <MenuItem disabled>No departments found</MenuItem>
                  ) : (
                    (Array.isArray(departments)
                      ? departments
                      : departments.results
                    )?.map((dept) => (
                      <MenuItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {departmentsError && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    Error loading departments: {departmentsError.message}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("ipd.admissions.expected_discharge_date")}
                type="date"
                value={formData.expected_discharge_date}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    expected_discharge_date: e.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("ipd.admissions.diagnosis")}
                multiline
                rows={3}
                value={formData.admission_diagnosis}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    admission_diagnosis: e.target.value,
                  });
                  setErrors({ ...errors, admission_diagnosis: "" });
                }}
                error={!!errors.admission_diagnosis}
                helperText={errors.admission_diagnosis}
                InputProps={{
                  startAdornment: <Description />,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("ipd.admissions.notes")}
                multiline
                rows={3}
                value={formData.admission_notes}
                onChange={(e) =>
                  setFormData({ ...formData, admission_notes: e.target.value })
                }
              />
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("ipd.admissions.review_admission")}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t("ipd.admissions.patient_info")}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Person color="primary" />
                    <Typography variant="body1">
                      {formData.patient?.firstname} {formData.patient?.lastname}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {formData.patient?.phone}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t("ipd.admissions.bed_info")}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Bed color="primary" />
                    <Typography variant="body1">
                      {formData.bed?.bed_number}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {formData.bed?.room_number} - {formData.bed?.ward_name}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t("ipd.admissions.admission_details")}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t("ipd.admissions.type_label")}:
                      </Typography>
                      <Typography variant="body1">
                        {formData.admission_type === 1 &&
                          t("ipd.admissions.type.emergency")}
                        {formData.admission_type === 2 &&
                          t("ipd.admissions.type.elective")}
                        {formData.admission_type === 3 &&
                          t("ipd.admissions.type.transfer")}
                        {formData.admission_type === 4 &&
                          t("ipd.admissions.type.observation")}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t("ipd.admissions.diagnosis")}:
                      </Typography>
                      <Typography variant="body1">
                        {formData.admission_diagnosis}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Back button and Title */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/ipd/admissions")}
        >
          {t("common.back")}
        </Button>
        <Typography variant="h4" component="h1">
          {t("ipd.admissions.new_admission")}
        </Typography>
        {emergencyVisit && (
          <Chip
            label={t("ipd.admissions.from_emergency", {
              defaultValue: "From Emergency",
            })}
            color="warning"
            variant="outlined"
            sx={{ ml: "auto" }}
          />
        )}
        {loadingEmergency && (
          <Box display="flex" alignItems="center" gap={1} sx={{ ml: "auto" }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              {t("ipd.admissions.loading_emergency", {
                defaultValue: "Loading emergency visit...",
              })}
            </Typography>
          </Box>
        )}
      </Box>

      <Paper sx={{ p: 3 }}>
        {/* Progress Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">{label}</Typography>
                  {index < activeStep && (
                    <CheckCircle color="success" fontSize="small" />
                  )}
                </Box>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Top Navigation Buttons - Easy Access */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<NavigateBefore />}
            variant="outlined"
          >
            {t("common.previous")}
          </Button>

          <Box display="flex" gap={2}>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={createMutation.isPending}
                startIcon={
                  createMutation.isPending ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Save />
                  )
                }
                size="large"
              >
                {createMutation.isPending
                  ? t("common.saving")
                  : t("common.save")}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<NavigateNext />}
                size="large"
              >
                {t("common.next")}
              </Button>
            )}
          </Box>
        </Box>

        {/* Step Content */}
        {renderStepContent(activeStep)}

        {/* Error Display */}
        {createMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {createMutation.error?.message ||
              t("ipd.admissions.error_creating")}
          </Alert>
        )}

        {/* Floating Action Button for Mobile */}
        <Fab
          color="primary"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            display: { xs: "flex", md: "none" },
            zIndex: 1000,
          }}
          onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
          disabled={createMutation.isPending}
        >
          {activeStep === steps.length - 1 ? (
            createMutation.isPending ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <Save />
            )
          ) : (
            <NavigateNext />
          )}
        </Fab>
      </Paper>
    </Box>
  );
};

export default NewAdmission;
